import { useState, useCallback } from 'react';
import { generateMovieUrl, generateReleaseUrl } from '../utils/urlUtils';

export const useCommunityData = (supabase, currentUser) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to format time ago
  const formatTimeAgo = useCallback(timestamp => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInMinutes = Math.floor((now - past) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }, []);

  // Fetch real activity feed from database
  const loadActivityFeed = useCallback(async () => {
    if (!supabase) return [];

    try {
      const activities = [];

      // 1. Recent collection additions (last 10)
      const { data: collectionActivities, error: collectionError } = await supabase
        .from('user_collection_items')
        .select(
          `
          id,
          created_at,
          user_id,
          movie_releases (
            id,
            display_id,
            release_title,
            studio,
            distributor_code,
            media_titles!master_movie_id (
              id,
              display_id,
              title,
              year
            )
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(10);

      if (!collectionError && collectionActivities) {
        // Get unique user IDs from collection activities
        const userIds = [
          ...new Set(collectionActivities.map(item => item.user_id).filter(Boolean)),
        ];

        // Fetch user profiles separately
        const { data: userProfiles } =
          userIds.length > 0
            ? await supabase
                .from('user_profiles')
                .select('user_id, username')
                .in('user_id', userIds)
            : { data: [] };

        // Create user map
        const userMap = {};
        userProfiles?.forEach(profile => {
          userMap[profile.user_id] = profile.username;
        });

        collectionActivities.forEach(item => {
          if (item.movie_releases?.media_titles) {
            const movieTitle = item.movie_releases.media_titles.title;
            const movie = {
              display_id: item.movie_releases.media_titles.display_id,
              title: movieTitle,
              year: item.movie_releases.media_titles.year,
            };

            // Format like trade listings: "Movie Title • Distributor Code"
            let displayTitle = movieTitle;
            if (item.movie_releases.studio) {
              displayTitle += ` • ${item.movie_releases.studio}`;
              if (item.movie_releases.distributor_code) {
                displayTitle += ` ${item.movie_releases.distributor_code}`;
              }
            }

            activities.push({
              id: `collection_${item.id}`,
              user: item.user_id ? userMap[item.user_id] || 'Unknown User' : '[deleted]',
              action: 'added to collection',
              item: displayTitle,
              time: formatTimeAgo(item.created_at),
              type: 'collection',
              url: generateMovieUrl(movie),
              movie_id: item.movie_releases.media_titles.id,
              release_id: item.movie_releases.id,
              movie_title: movieTitle,
              created_at: item.created_at,
            });
          }
        });
      }

      // 2. Recent marketplace listings (last 10)
      const { data: marketplaceActivities, error: marketplaceError } = await supabase
        .from('marketplace_listings')
        .select(
          `
          id,
          created_at,
          seller_id,
          movie_releases (
            id,
            display_id,
            media_titles!master_movie_id (
              id,
              display_id,
              title,
              year
            )
          )
        `
        )
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!marketplaceError && marketplaceActivities) {
        // Get unique seller IDs
        const sellerIds = [
          ...new Set(marketplaceActivities.map(item => item.seller_id).filter(Boolean)),
        ];

        // Fetch seller profiles separately
        const { data: sellerProfiles } =
          sellerIds.length > 0
            ? await supabase
                .from('user_profiles')
                .select('user_id, username')
                .in('user_id', sellerIds)
            : { data: [] };

        // Create seller map
        const sellerMap = {};
        sellerProfiles?.forEach(profile => {
          sellerMap[profile.user_id] = profile.username;
        });

        marketplaceActivities.forEach(item => {
          if (item.movie_releases?.media_titles) {
            const movie = {
              display_id: item.movie_releases.media_titles.display_id,
              title: item.movie_releases.media_titles.title,
              year: item.movie_releases.media_titles.year,
            };

            activities.push({
              id: `marketplace_${item.id}`,
              user: item.seller_id ? sellerMap[item.seller_id] || 'Unknown User' : '[deleted]',
              action: 'listed for sale',
              item: item.movie_releases.media_titles.title,
              time: formatTimeAgo(item.created_at),
              type: 'marketplace',
              url: generateMovieUrl(movie),
              movie_id: item.movie_releases.media_titles.id,
              release_id: item.movie_releases.id,
              movie_title: item.movie_releases.media_titles.title,
              created_at: item.created_at,
            });
          }
        });
      }

      // 3. Recent catalog additions (new releases created) (last 10)
      const { data: catalogActivities, error: catalogError } = await supabase
        .from('movie_releases')
        .select(
          `
          id,
          display_id,
          created_at,
          release_title,
          studio,
          distributor_code,
          added_by,
          media_titles!master_movie_id (
            id,
            display_id,
            title,
            year
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(10);

      if (!catalogError && catalogActivities) {
        // Get unique user IDs from catalog activities
        const catalogUserIds = [
          ...new Set(catalogActivities.map(item => item.added_by).filter(Boolean)),
        ];

        // Fetch user profiles separately
        const { data: catalogUserProfiles } =
          catalogUserIds.length > 0
            ? await supabase
                .from('user_profiles')
                .select('user_id, username')
                .in('user_id', catalogUserIds)
            : { data: [] };

        // Create user map
        const catalogUserMap = {};
        catalogUserProfiles?.forEach(profile => {
          catalogUserMap[profile.user_id] = profile.username;
        });

        catalogActivities.forEach(item => {
          if (item.media_titles) {
            const movieTitle = item.media_titles.title;
            const movie = {
              display_id: item.media_titles.display_id,
              title: movieTitle,
              year: item.media_titles.year,
            };
            const release = {
              display_id: item.display_id,
            };

            // Format like trade listings: "Movie Title • Distributor Code"
            let displayTitle = movieTitle;
            if (item.studio) {
              displayTitle += ` • ${item.studio}`;
              if (item.distributor_code) {
                displayTitle += ` ${item.distributor_code}`;
              }
            }

            activities.push({
              id: `catalog_${item.id}`,
              user: item.added_by ? catalogUserMap[item.added_by] || 'Unknown User' : '[deleted]',
              action: 'added release to catalog',
              item: displayTitle,
              time: formatTimeAgo(item.created_at),
              type: 'catalog',
              url: generateReleaseUrl(movie, release),
              movie_id: item.media_titles.id,
              release_id: item.id,
              movie_title: movieTitle,
              release_title: item.release_title,
              created_at: item.created_at,
            });
          }
        });
      }

      // 4. Recent master movie additions (movies without releases) (last 10)
      const { data: masterMovieActivities, error: masterMovieError } = await supabase
        .from('media_titles')
        .select(
          `
          id,
          display_id,
          created_at,
          title,
          year,
          added_by
        `
        )
        .order('created_at', { ascending: false })
        .limit(10);

      if (!masterMovieError && masterMovieActivities) {
        // Get unique user IDs from master movie activities
        const masterMovieUserIds = [
          ...new Set(masterMovieActivities.map(item => item.added_by).filter(Boolean)),
        ];

        // Fetch user profiles separately
        const { data: masterMovieUserProfiles, error: masterMovieUserError } = await supabase
          .from('user_profiles')
          .select('user_id, username')
          .in('user_id', masterMovieUserIds);

        // Create user map
        const masterMovieUserMap = {};
        masterMovieUserProfiles?.forEach(profile => {
          masterMovieUserMap[profile.user_id] = profile.username;
        });

        masterMovieActivities.forEach(item => {
          if (item.added_by) {
            const movieTitle = item.title;
            const year = item.year;
            const movie = {
              display_id: item.display_id,
              title: movieTitle,
              year: year,
            };

            // Format: "Movie Title (Year)"
            let displayTitle = movieTitle;
            if (year) {
              displayTitle += ` (${year})`;
            }

            activities.push({
              id: `master_movie_${item.id}`,
              user: item.added_by
                ? masterMovieUserMap[item.added_by] || 'Unknown User'
                : '[deleted]',
              action: 'added movie to catalog',
              item: displayTitle,
              time: formatTimeAgo(item.created_at),
              type: 'master_movie',
              url: generateMovieUrl(movie),
              movie_id: item.id,
              movie_title: movieTitle,
              created_at: item.created_at,
            });
          }
        });
      }

      // 5. Recent forum posts (last 10)
      const { data: forumActivities, error: forumError } = await supabase
        .from('forum_posts')
        .select(
          `
          id,
          created_at,
          title,
          user_id
        `
        )
        .order('created_at', { ascending: false })
        .limit(10);

      if (!forumError && forumActivities) {
        // Get unique user IDs
        const forumUserIds = [
          ...new Set(forumActivities.map(post => post.user_id).filter(Boolean)),
        ];

        // Fetch user profiles separately
        const { data: forumUserProfiles } =
          forumUserIds.length > 0
            ? await supabase
                .from('user_profiles')
                .select('user_id, username')
                .in('user_id', forumUserIds)
            : { data: [] };

        // Create user map
        const forumUserMap = {};
        forumUserProfiles?.forEach(profile => {
          forumUserMap[profile.user_id] = profile.username;
        });

        forumActivities.forEach(post => {
          activities.push({
            id: `forum_${post.id}`,
            user: post.user_id ? forumUserMap[post.user_id] || 'Unknown User' : '[deleted]',
            action: 'started discussion',
            item: post.title,
            time: formatTimeAgo(post.created_at),
            type: 'forum',
          });
        });
      }

      // Sort all activities by creation time (most recent first)
      activities.sort((a, b) => {
        // Convert time strings back to comparable values for sorting
        const timeToMinutes = timeStr => {
          if (timeStr === 'Just now') return 0;
          if (timeStr.includes('min')) return parseInt(timeStr);
          if (timeStr.includes('hour')) return parseInt(timeStr) * 60;
          if (timeStr.includes('day')) return parseInt(timeStr) * 24 * 60;
          return 99999; // fallback for unknown formats
        };
        return timeToMinutes(a.time) - timeToMinutes(b.time);
      });

      const finalActivities = activities.slice(0, 20);
      return finalActivities;
    } catch (error) {
      console.error('Error loading activity feed:', error);
      return [];
    }
  }, [supabase, formatTimeAgo]);

  // Fetch community statistics
  const loadCommunityStats = useCallback(async () => {
    if (!supabase) return null;

    try {
      setLoading(true);

      // Count total users
      const { count: userCount } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      // Count active trade listings
      const { count: tradeCount } = await supabase
        .from('trade_listings')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Count total forum posts
      const { count: postCount } = await supabase
        .from('forum_posts')
        .select('*', { count: 'exact', head: true });

      return {
        totalUsers: userCount || 0,
        activeTrades: tradeCount || 0,
        recentPosts: postCount || 0,
        challengeProgress: 69,
      };
    } catch (error) {
      console.error('Error loading community stats:', error);
      setError(error);
      return {
        totalUsers: 0,
        activeTrades: 0,
        recentPosts: 0,
        challengeProgress: 69,
      };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Load trade requests from marketplace listings
  const loadTradeRequests = useCallback(async () => {
    if (!supabase) return [];

    try {
      // Load from our dedicated trade_listings table - simplified query
      const { data: listings, error } = await supabase
        .from('trade_listings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error || !listings) return [];

      // Add user_name from trader_id lookup
      const listingsWithUserNames = await Promise.all(
        listings.map(async listing => {
          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('username')
              .eq('id', listing.trader_id)
              .single();

            return {
              ...listing,
              user_name: profile?.username || 'Unknown User',
            };
          } catch (error) {
            return {
              ...listing,
              user_name: 'Unknown User',
            };
          }
        })
      );

      return listingsWithUserNames;
    } catch (error) {
      console.error('Error loading trade requests:', error);
      return [];
    }
  }, [supabase, formatTimeAgo]);

  // Load challenges
  const loadChallenges = useCallback(async () => {
    if (!supabase) return [];

    try {
      const { data: challenges, error } = await supabase
        .from('challenges')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading challenges:', error);
        return [];
      }

      return challenges || [];
    } catch (error) {
      console.error('Error loading challenges:', error);
      return [];
    }
  }, [supabase]);

  // Load top collectors (real data)
  const loadTopCollectors = useCallback(async () => {
    if (!supabase) return [];

    try {
      // Get users with their collection counts
      const { data: collectors, error } = await supabase
        .from('user_profiles')
        .select(
          `
          user_id,
          username,
          avatar_url,
          bio,
          created_at,
          user_collection_items!user_collection_items_user_id_fkey (
            id
          ),
          user_wantlist_items!user_wantlist_items_user_id_fkey (
            id
          )
        `
        )
        .limit(50);

      if (error || !collectors) return [];

      // Process and sort by collection size
      const processedCollectors = collectors
        .map(collector => ({
          user_id: collector.user_id,
          name: collector.username || 'Unknown',
          collection: collector.user_collection_items?.length || 0,
          wantlist: collector.user_wantlist_items?.length || 0,
          avatar_url: collector.avatar_url,
          joinDate: collector.created_at,
          status: 'active', // Could be calculated based on recent activity
        }))
        .filter(collector => collector.collection > 0) // Only show users with collections
        .sort((a, b) => b.collection - a.collection)
        .slice(0, 10); // Top 10

      return processedCollectors;
    } catch (error) {
      console.error('Error loading top collectors:', error);
      return [];
    }
  }, [supabase]);

  // Load collection overlaps (movies in common with other users)
  const loadCollectionOverlaps = useCallback(
    async userCollectionItems => {
      if (!supabase || !currentUser || !userCollectionItems?.length) return [];

      try {
        // Get all release IDs from user's collection
        const userReleaseIds = userCollectionItems.map(item => item.release_id).filter(Boolean);

        if (userReleaseIds.length === 0) return [];

        // Find other users who have the same releases
        const { data: overlaps, error } = await supabase
          .from('user_collection_items')
          .select(
            `
          release_id,
          user_id,
          movie_releases!inner (
            id,
            media_titles!master_movie_id (
              title
            )
          )
        `
          )
          .in('release_id', userReleaseIds)
          .neq('user_id', currentUser.id); // Exclude current user

        if (error || !overlaps) {
          console.error('Error loading collection overlaps:', error);
          return [];
        }

        // Count how many other users have each movie
        const releaseCount = {};
        overlaps.forEach(item => {
          const releaseId = item.release_id;
          const title = item.movie_releases?.media_titles?.title;

          if (title) {
            if (!releaseCount[releaseId]) {
              releaseCount[releaseId] = {
                title,
                count: new Set(),
              };
            }
            releaseCount[releaseId].count.add(item.user_id);
          }
        });

        // Convert to array and sort by popularity
        const overlapList = Object.entries(releaseCount)
          .map(([releaseId, data]) => ({
            title: data.title,
            count: data.count.size,
          }))
          .filter(item => item.count > 0) // Only show movies that others have
          .sort((a, b) => b.count - a.count) // Sort by most popular first
          .slice(0, 5); // Top 5

        return overlapList;
      } catch (error) {
        console.error('Error loading collection overlaps:', error);
        return [];
      }
    },
    [supabase, currentUser]
  );

  // Load trade opportunities (wantlist items available in marketplace)
  const loadTradeOpportunities = useCallback(
    async userWantlist => {
      if (!supabase || !currentUser || !userWantlist?.length) return [];

      try {
        // Get all release IDs from user's wantlist
        const wantedReleaseIds = userWantlist.map(item => item.release_id).filter(Boolean);

        if (wantedReleaseIds.length === 0) return [];

        // Find active marketplace listings for wanted items
        const { data: availableItems, error } = await supabase
          .from('marketplace_listings')
          .select(
            `
          release_id,
          seller_id,
          price,
          condition,
          movie_releases!inner (
            id,
            media_titles!master_movie_id (
              title
            )
          )
        `
          )
          .in('release_id', wantedReleaseIds)
          .eq('is_active', true)
          .neq('seller_id', currentUser.id); // Exclude current user's listings

        if (error || !availableItems) {
          console.error('Error loading trade opportunities:', error);
          return [];
        }

        // Count available listings per movie
        const availabilityCount = {};
        availableItems.forEach(item => {
          const releaseId = item.release_id;
          const title = item.movie_releases?.media_titles?.title;

          if (title) {
            if (!availabilityCount[releaseId]) {
              availabilityCount[releaseId] = {
                title,
                count: 0,
                lowestPrice: item.price,
              };
            }
            availabilityCount[releaseId].count += 1;
            if (
              item.price &&
              (!availabilityCount[releaseId].lowestPrice ||
                item.price < availabilityCount[releaseId].lowestPrice)
            ) {
              availabilityCount[releaseId].lowestPrice = item.price;
            }
          }
        });

        // Convert to array and sort by availability
        const opportunitiesList = Object.entries(availabilityCount)
          .map(([releaseId, data]) => ({
            title: data.title,
            count: data.count,
            lowestPrice: data.lowestPrice,
          }))
          .sort((a, b) => b.count - a.count) // Sort by most available first
          .slice(0, 5); // Top 5

        return opportunitiesList;
      } catch (error) {
        console.error('Error loading trade opportunities:', error);
        return [];
      }
    },
    [supabase, currentUser]
  );

  return {
    loadActivityFeed,
    loadCommunityStats,
    loadTradeRequests,
    loadChallenges,
    loadTopCollectors,
    loadCollectionOverlaps,
    loadTradeOpportunities,
    loading,
    error,
  };
};
